'use client';

import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@pickleballinc/ui/components/ui/button';
import { Panel, PanelContent, PanelHeader, PanelBody, PanelClose, PanelTitle, PanelDescription } from '@pickleballinc/ui/components/ui/panel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pickleballinc/ui/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@pickleballinc/ui/components/ui/tooltip';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { DatetimePickerForm } from '@/components/molecules/DatetimePickerForm';
import { MatchCompletedEnum } from '@/entities/matchup/constants';
import { SystemLocationsCourts } from '@/entities/SystemLocationsCourts';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { matchKeys } from '@/hooks/matches/matchKeys';
import { useUpdateMatchMutation } from '@/hooks/matches/useUpdateMatch';
import { useOrganizationStore } from '@/store/organization-store';
import { formatTeamLeagueLabels } from '@/utils/helpers';

interface CourtAndTimeModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	courts: SystemLocationsCourts[];
	match: {
		matchUuid: string;
		courtUuid?: string;
		matchPlannedStart?: string;
		matchAssignedToCourt?: string;
	};
	moduleUuid: string;
	locationUuid: string;
	matchupUuid: string;
}

const matchTimeAndCourtFormSchema = z.object({
	matchUuid: z.string(),
	courtUuid: z.string().nullish(),
	matchPlannedStart: z.string().nullish(),
	date_assigned_to_court: z.string().nullish(),
	matchStatus: z.number().nullish(),
	matchCompletedType: z.nativeEnum(MatchCompletedEnum).nullish()
});

type MatchTimeAndCourtFormType = z.infer<typeof matchTimeAndCourtFormSchema>;

export const CourtAndTimeModal = ({ open, onOpenChange, courts, match, matchupUuid }: CourtAndTimeModalProps) => {
	const store = useOrganizationStore();
	const tlTimeZone = store.teamLeagueTimeZone;
	const queryClient = useQueryClient();
	const teamLeagueInfo = useTeamLeagueInfo();
	const labels = formatTeamLeagueLabels(teamLeagueInfo);

	const form = useForm<MatchTimeAndCourtFormType>({
		resolver: zodResolver(matchTimeAndCourtFormSchema),
		values: {
			matchUuid: match.matchUuid,
			matchPlannedStart: match.matchPlannedStart
		}
	});

	const [selectedCourt, setSelectedCourt] = useState<string | undefined>(match?.courtUuid);

	const { mutate: updateCourtMutation, isPending: isUpdatingCourt } = useUpdateMatchMutation({
		onSuccessCallback: () => {
			toast.success('Successfully updated');
			onOpenChange(false);
		}
	});

	const handleSave = form.handleSubmit((data: MatchTimeAndCourtFormType) => {
		try {
			updateCourtMutation(
				{
					matchId: match.matchUuid,
					updateData: {
						plannedStartDate: data.matchPlannedStart || '',
						courtUuid: selectedCourt || '',
						dateAssignedToCourt: match.courtUuid === selectedCourt ? match.matchAssignedToCourt : new Date().toISOString()
					}
				},
				{
					onSuccess: () => {
						queryClient.invalidateQueries({
							queryKey: matchKeys.matches(matchupUuid)
						});
					}
				}
			);
		} catch (error) {
			toast.error('Something went wrong and we were not able to save the changes.');
		}
	});

	const isDisabled = !form.formState.isDirty && match.courtUuid === selectedCourt && match.matchPlannedStart === form.watch('matchPlannedStart');
	const availableCourts = courts.map((court) => ({ label: court.title, value: court.uuid })) ?? [];

	return (
		<FormProvider {...form}>
			<Panel open={open} onOpenChange={onOpenChange}>
				<PanelContent className="flex w-full flex-col md:w-96 xl:w-100 2xl:w-120" aria-describedby="court-assignment-description">
					<PanelHeader className="flex flex-col px-6 pb-2 pt-4 border-b border-gray-200">
						<div className="flex items-center justify-between">
							<PanelTitle asChild>
								<h2 className="text-2xl font-semibold text-gray-900">Assign a court</h2>
							</PanelTitle>

							<PanelClose asChild>
								<button
									className="size-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
									aria-label="Close dialog"
								>
									<FontAwesomeIcon icon={faXmark} className="size-5 text-gray-700" />
								</button>
							</PanelClose>
						</div>
						<PanelDescription className="text-base font-normal text-gray-600 text-start">
							Court assigning for the {labels.tieBreakerMatchTitle}.
						</PanelDescription>
					</PanelHeader>

					<PanelBody className="flex-1 px-6 pt-4 pb-8">
						<div className="flex h-full flex-col">
							<div className="flex-1 space-y-4">
								<div className="flex flex-col gap-2">
									<label htmlFor="matchPlannedStart" className="text-sm font-medium text-gray-700">
										Date
									</label>
									<DatetimePickerForm
										name="matchPlannedStart"
										className="w-full border-gray-300 shadow-sm rounded-md"
										placeholder="Select date and time"
										ianaTzIdentifier={tlTimeZone?.ianaTzIdentifier}
									/>
								</div>

								<div className="flex flex-col gap-2">
									<label htmlFor="courtUuid" className="text-sm font-medium text-gray-700">
										Court
									</label>
									<Select onValueChange={(value) => setSelectedCourt(value)} value={selectedCourt}>
										<SelectTrigger
											className="bg-white focus:ring-0"
											icon={({ className }: { className: string }) => (
												<FontAwesomeIcon icon={faChevronDown} className={className} />
											)}
										>
											<SelectValue placeholder="Select court" />
										</SelectTrigger>
										<SelectContent>
											{availableCourts.map((court) => (
												<SelectItem key={court.value} value={court.value}>
													{court.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>

							<div className="flex flex-row gap-4">
								<Button
									onClick={() => onOpenChange(false)}
									className="!w-full justify-center text-base font-semibold shadow-xs transition-all duration-200 hover:shadow-sm"
									disabled={isUpdatingCourt}
									variant="outline"
								>
									Cancel
								</Button>
								{isDisabled ? (
									<TooltipProvider delayDuration={0} skipDelayDuration={0}>
										<Tooltip>
											<TooltipTrigger asChild>
												<Button
													className={
														'w-full text-base font-semibold shadow-xs transition-all duration-200 border border-gray-200 !text-gray-400 !bg-gray-100 disabled:pointer-events-auto'
													}
													disabled
												>
													Save
												</Button>
											</TooltipTrigger>
											<TooltipContent className="bg-gray-900">
												<p>To enable saving, change the time or court first</p>
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								) : (
									<Button
										onClick={handleSave}
										className={
											'w-full text-base font-semibold shadow-xs transition-all duration-200 border border-gray-200 hover:shadow-sm'
										}
										disabled={isDisabled || form.formState.isSubmitting || isUpdatingCourt}
									>
										Save
									</Button>
								)}
							</div>
						</div>
					</PanelBody>
				</PanelContent>
			</Panel>
		</FormProvider>
	);
};
